import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  ChartBarIcon,
  ArrowTrendingDownIcon,
  ArrowsRightLeftIcon,
  CircleStackIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
  RocketLaunchIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import type {
  ROIDashboardProps,
  InsightData,
  InsightType,
  InsightSubtype,
  MiniViz as MiniVizData,
  FiabilisationData,
  EconomyData,
  AdoptionData as AdoptionDataType,
  CapitalizationData,
  ScopeData,
  DataMaturity,
} from './types';
import {
  mockNoHistory,
  mockLowHistory,
  mockRichHistory,
  mockMatureClient,
} from './mockROIData';
import {
  formatEuro,
  formatEuroRangeCompact,
  formatNumber,
  formatPeriod,
} from './utils/format';

// -------------------------------------------------------------------
// ObservatoireROI — page wrapper with profile switcher
// -------------------------------------------------------------------

type ProfileId = 'no-history' | 'low' | 'rich' | 'mature';

interface ProfileEntry {
  id: ProfileId;
  label: string;
  count: number;
  data: ROIDashboardProps;
}

const PROFILES: ProfileEntry[] = [
  { id: 'no-history', label: 'Pas d’historique', count: 0, data: mockNoHistory },
  { id: 'low', label: 'Historique faible', count: 2, data: mockLowHistory },
  { id: 'rich', label: 'Client actif', count: 5, data: mockRichHistory },
  { id: 'mature', label: 'Client mature', count: 18, data: mockMatureClient },
];

const PROFILE_IDS = PROFILES.map((p) => p.id);

const getInitialProfileId = (): ProfileId => {
  if (typeof window === 'undefined') return 'rich';
  const param = new URLSearchParams(window.location.search).get('profile');
  if (param && (PROFILE_IDS as string[]).includes(param)) return param as ProfileId;
  return 'rich';
};

const updateProfileInUrl = (id: ProfileId): void => {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  params.set('profile', id);
  const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.replaceState(null, '', newUrl);
};

const ObservatoireROI: React.FC = () => {
  const [profileId, setProfileId] = useState<ProfileId>(getInitialProfileId);
  const active = PROFILES.find((p) => p.id === profileId) ?? PROFILES[2];

  const handleProfileChange = (id: ProfileId) => {
    setProfileId(id);
    updateProfileInUrl(id);
  };

  const handleRequestDeployment = () => {
    window.location.href = 'mailto:hello@sillant.com?subject=Planifier%20le%20d%C3%A9ploiement%20Sillant';
  };

  return (
    <div className="relative min-h-screen bg-[#FAFAFA] text-[#2F2F2F]">
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10">
        <ProfilePreview profiles={PROFILES} activeId={profileId} onChange={handleProfileChange} />
      </div>
      <ROIDashboard
        {...active.data}
        onRequestDeployment={handleRequestDeployment}
      />
    </div>
  );
};

// -------------------------------------------------------------------
// ProfilePreview — discreet icon button + popover (démo tool)
// -------------------------------------------------------------------

interface ProfilePreviewProps {
  profiles: ProfileEntry[];
  activeId: ProfileId;
  onChange: (id: ProfileId) => void;
}

const ProfilePreview: React.FC<ProfilePreviewProps> = ({ profiles, activeId, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Aperçu démo"
        aria-expanded={open}
        aria-haspopup="menu"
        className="p-2 rounded-full text-[#9A9A9A] hover:text-[#2F2F2F] hover:bg-white/80 transition-colors"
      >
        <EyeIcon className="w-5 h-5" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-2 bg-white border border-[#E8E8E8] rounded-xl shadow-lg overflow-hidden min-w-[220px]"
        >
          {profiles.map((p) => {
            const isActive = p.id === activeId;
            return (
              <button
                key={p.id}
                role="menuitem"
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                }}
                className={
                  'w-full text-left px-4 py-2.5 text-sm transition-colors ' +
                  (isActive
                    ? 'bg-[#F5F0FF] text-[#8b5cf6] font-medium'
                    : 'text-[#2F2F2F] hover:bg-gray-50')
                }
              >
                {p.label} <span className={isActive ? 'opacity-70' : 'text-[#9A9A9A]'}>({p.count})</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// ROIDashboard — core component
// -------------------------------------------------------------------

const ROIDashboard: React.FC<ROIDashboardProps> = ({
  mode,
  dataMaturity,
  fiabilisation,
  economy,
  insights,
  adoption,
  capitalization,
  scope,
  onRequestDeployment,
  onDownloadReport,
}) => {
  const showEconomy = !!economy && dataMaturity.auditedOperationsCount > 0;

  return (
    <>
      <DualHero
        fiabilisation={fiabilisation}
        economy={showEconomy ? economy : undefined}
        dataMaturity={dataMaturity}
      />
      <InsightsGrid insights={insights} />
      <AdoptionCapitalization
        adoption={adoption}
        capitalization={capitalization}
        capitalizationMonths={dataMaturity.capitalizationMonths}
        hasAdoptionData={dataMaturity.hasAdoptionData}
        onRequestDeployment={onRequestDeployment}
      />
      <AuditFooter
        scope={scope}
        mode={mode}
        onRequestDeployment={onRequestDeployment}
        onDownloadReport={onDownloadReport}
      />
    </>
  );
};

// -------------------------------------------------------------------
// DualHero
// -------------------------------------------------------------------

interface DualHeroProps {
  fiabilisation: FiabilisationData;
  economy?: EconomyData;
  dataMaturity: DataMaturity;
}

const DualHero: React.FC<DualHeroProps> = ({ fiabilisation, economy, dataMaturity }) => {
  const isSolo = !economy;

  if (isSolo) {
    return (
      <section className="bg-[#FAFAFA] py-16 md:py-20 px-4 md:px-12">
        <div className="max-w-[720px] mx-auto">
          <FiabilisationHero fiabilisation={fiabilisation} solo capitalizationMonths={dataMaturity.capitalizationMonths} />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#FAFAFA] py-10 md:py-16 px-4 md:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        <FiabilisationHero fiabilisation={fiabilisation} solo={false} capitalizationMonths={dataMaturity.capitalizationMonths} />
        <EconomyHero economy={economy} typologyRelevance={dataMaturity.typologyRelevance} />
      </div>
    </section>
  );
};

// -------------------------------------------------------------------
// FiabilisationHero
// -------------------------------------------------------------------

interface FiabilisationHeroProps {
  fiabilisation: FiabilisationData;
  solo: boolean;
  capitalizationMonths: number;
}

const FiabilisationHero: React.FC<FiabilisationHeroProps> = ({ fiabilisation, solo, capitalizationMonths }) => {
  const { capitalizedPrices, analyzedOffers, documentedCostEstimates } = fiabilisation;

  if (solo) {
    const isEmpty = capitalizedPrices === 0;
    return (
      <div className="bg-white border border-[#E8E8E8] rounded-2xl p-8 md:p-10 text-center">
        <div className="text-xs uppercase tracking-wider text-[#8b5cf6] font-medium">
          Votre mémoire propriétaire
        </div>
        {isEmpty ? (
          <>
            <div className="mt-6 text-[40px] md:text-[56px] leading-tight font-medium">
              Votre bible démarre aujourd’hui
            </div>
            <p className="mt-4 text-base text-[#6B6B6B] leading-relaxed">
              Chaque DCE analysé par Sillant enrichira votre référentiel propriétaire.
              Structurez, capitalisez, comparez — sans jamais repartir de zéro.
            </p>
          </>
        ) : (
          <>
            <div className="mt-6 text-[48px] md:text-[72px] leading-none font-medium tracking-tight">
              {formatNumber(capitalizedPrices)}
            </div>
            <div className="mt-3 text-base text-[#2F2F2F]">
              prix capitalisés et structurés
            </div>
            <div className="mt-4 text-sm text-[#6B6B6B]">
              {formatNumber(analyzedOffers)} offres analysées · {formatNumber(documentedCostEstimates)} chiffrages documentés
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E8E8E8] rounded-2xl p-8 md:p-10">
      <div className="text-xs uppercase tracking-wider text-[#8b5cf6] font-medium">
        Votre patrimoine de données structurées
      </div>
      <div className="mt-8 grid grid-cols-3 gap-4">
        <HeroMetric value={formatNumber(capitalizedPrices)} label="prix capitalisés" />
        <HeroMetric value={formatNumber(analyzedOffers)} label="offres analysées" />
        <HeroMetric value={formatNumber(documentedCostEstimates)} label="chiffrages documentés" />
      </div>
      {capitalizationMonths > 0 && (
        <div className="mt-6 text-sm text-[#6B6B6B]">
          Construit sur {capitalizationMonths} mois d’utilisation
        </div>
      )}
    </div>
  );
};

const HeroMetric: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <div className="text-[28px] md:text-[40px] leading-tight font-medium tracking-tight">{value}</div>
    <div className="mt-1 text-xs md:text-sm text-[#6B6B6B] leading-tight">{label}</div>
  </div>
);

// -------------------------------------------------------------------
// EconomyHero
// -------------------------------------------------------------------

interface EconomyHeroProps {
  economy: EconomyData;
  typologyRelevance: DataMaturity['typologyRelevance'];
}

const EconomyHero: React.FC<EconomyHeroProps> = ({ economy, typologyRelevance }) => {
  const { minAnnual, maxAnnual, operationsAudited, operationsPerYear, confidenceLevel } = economy;
  const isPreliminary = confidenceLevel === 'preliminary';

  return (
    <div className="bg-white border border-[#E8E8E8] rounded-2xl p-8 md:p-10">
      <div className="text-xs uppercase tracking-wider text-[#E8875A] font-medium">
        {isPreliminary ? 'Estimation préliminaire' : 'Potentiel d’économie identifié sur 12 mois'}
      </div>
      <div className="mt-6 text-[36px] md:text-[48px] leading-none font-medium tracking-tight">
        {formatEuroRangeCompact(minAnnual, maxAnnual)}
      </div>
      <div className="mt-4 text-base text-[#6B6B6B] leading-relaxed">
        Basé sur {operationsAudited} opération{operationsAudited > 1 ? 's' : ''} auditée{operationsAudited > 1 ? 's' : ''}
        {' '}· {operationsPerYear}/an projetées
      </div>
      {typologyRelevance === 'low' && (
        <div className="mt-5 inline-block bg-[#FFF4ED] text-[#2F2F2F] text-sm px-3 py-1.5 rounded-full">
          Sur typologies historiques — à recalibrer sur pipeline futur
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// InsightsGrid
// -------------------------------------------------------------------

const InsightsGrid: React.FC<{ insights: readonly InsightData[] }> = ({ insights }) => (
  <section className="bg-[#FAFAFA] pb-12 px-4 md:px-12">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
      {insights.map((ins, i) => (
        <InsightCard key={i} insight={ins} index={i} />
      ))}
    </div>
  </section>
);

// -------------------------------------------------------------------
// InsightCard
// -------------------------------------------------------------------

const subtypeIconMap: Record<InsightSubtype, typeof ChartBarIcon> = {
  dispersion: ChartBarIcon,
  bias: ArrowTrendingDownIcon,
  'inter-operation': ArrowsRightLeftIcon,
  structuration: CircleStackIcon,
  capitalisation: ArchiveBoxIcon,
  'oab-detection': ExclamationTriangleIcon,
};

const headlineColorForType = (type: InsightType): string => {
  if (type === 'fiabilite') return '#8b5cf6';
  if (type === 'economie') return '#E8875A';
  return '#2F2F2F';
};

const BadgeForType: React.FC<{ type: InsightType }> = ({ type }) => {
  if (type === 'fiabilite') {
    return (
      <span className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-full bg-[#D7C9FF] text-[#2F2F2F]">
        Fiabilité
      </span>
    );
  }
  if (type === 'economie') {
    return (
      <span className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-full bg-[#FFCBB3] text-[#2F2F2F]">
        Économie
      </span>
    );
  }
  return (
    <span
      className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-full text-[#2F2F2F]"
      style={{ background: 'linear-gradient(135deg, #D7C9FF 0%, #FFCBB3 100%)' }}
    >
      Fiabilité + Économie
    </span>
  );
};

const FooterValueColor: Record<'violet' | 'peach' | 'both', string> = {
  violet: '#8b5cf6',
  peach: '#E8875A',
  both: '#2F2F2F',
};

const InsightCard: React.FC<{ insight: InsightData; index: number }> = ({ insight, index }) => {
  const Icon = insight.subtype ? subtypeIconMap[insight.subtype] : ChartBarIcon;
  const headlineColor = headlineColorForType(insight.type);
  const footerColor = FooterValueColor[insight.footer.highlightColor];

  return (
    <article className="bg-white border border-[#E8E8E8] rounded-2xl p-6 md:p-8 flex flex-col transition-colors duration-200 hover:border-[#D7C9FF]">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#6B6B6B]" aria-hidden="true" />
          <span className="text-xs uppercase tracking-wider text-[#6B6B6B] font-medium">
            Insight {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <BadgeForType type={insight.type} />
      </header>

      <h3 className="mt-5 text-[22px] leading-tight font-medium">{insight.title}</h3>

      <div
        className="mt-4 text-[40px] leading-none font-medium tracking-tight"
        style={{ color: headlineColor }}
      >
        {insight.headlineValue}
        {insight.headlineUnit && (
          <span className="text-[24px] text-[#6B6B6B] ml-1">{insight.headlineUnit}</span>
        )}
      </div>
      <div className="mt-2 text-base text-[#2F2F2F] leading-relaxed">{insight.caption}</div>
      <div className="mt-1 text-sm text-[#6B6B6B]">{insight.context}</div>

      <div className="mt-5 h-[120px]">
        <MiniViz viz={insight.miniViz} insightType={insight.type} />
      </div>

      <div className="mt-5 pt-4 border-t border-[#E8E8E8]">
        <div className="text-xs uppercase tracking-wider text-[#6B6B6B] font-medium">
          {insight.footer.label}
        </div>
        <div className="mt-1 text-base font-medium" style={{ color: footerColor }}>
          {insight.footer.value}
        </div>
      </div>
    </article>
  );
};

// -------------------------------------------------------------------
// MiniViz — recharts-based mini visualizations
// -------------------------------------------------------------------

const PEACH = '#E8875A';
const VIOLET = '#8b5cf6';
const NEUTRAL = '#D4D4D4';

const barColor = (highlight: boolean | undefined, insightType: InsightType): string => {
  if (highlight) return insightType === 'fiabilite' ? VIOLET : PEACH;
  return NEUTRAL;
};

const MiniViz: React.FC<{ viz: MiniVizData; insightType: InsightType }> = ({ viz, insightType }) => {
  if (viz.type === 'bars') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={viz.data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6B6B6B' }}
            width={60}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip cursor={{ fill: '#F5F5F5' }} formatter={(v: number) => `${v} ${viz.unit}`} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {viz.data.map((d, i) => (
              <Cell key={i} fill={barColor(d.highlight, insightType)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (viz.type === 'comparison') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={viz.data} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B6B6B' }} axisLine={false} tickLine={false} />
          <YAxis hide />
          <Tooltip cursor={{ fill: '#F5F5F5' }} formatter={(v: number) => `${v} ${viz.unit}`} />
          <ReferenceLine y={0} stroke="#E8E8E8" />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
            {viz.data.map((d, i) => (
              <Cell key={i} fill={barColor(d.highlight, insightType)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (viz.type === 'scatter') {
    const sorted = [...viz.data].map((d) => d.value).sort((a, b) => a - b);
    const median =
      sorted.length === 0
        ? 0
        : sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];
    const points = viz.data.map((d, i) => ({ x: i + 1, y: d.value, highlight: d.highlight }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
          <XAxis type="number" dataKey="x" hide domain={[0, points.length + 1]} />
          <YAxis type="number" dataKey="y" tick={{ fontSize: 11, fill: '#6B6B6B' }} axisLine={false} tickLine={false} width={40} />
          <Tooltip cursor={{ stroke: '#E8E8E8' }} formatter={(v: number) => `${v} ${viz.unit}`} />
          <ReferenceLine y={median} stroke="#6B6B6B" strokeDasharray="3 3" />
          <Scatter data={points}>
            {points.map((p, i) => (
              <Cell key={i} fill={p.highlight ? PEACH : VIOLET} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  // progression
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={viz.data} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
        <defs>
          <linearGradient id={`areaGrad-${viz.unit}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={VIOLET} stopOpacity={0.5} />
            <stop offset="100%" stopColor={VIOLET} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B6B6B' }} axisLine={false} tickLine={false} />
        <YAxis hide />
        <Tooltip formatter={(v: number) => `${v} ${viz.unit}`} />
        <Area type="monotone" dataKey="value" stroke={VIOLET} strokeWidth={2} fill={`url(#areaGrad-${viz.unit})`} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// -------------------------------------------------------------------
// AdoptionCapitalization
// -------------------------------------------------------------------

interface AdoptionCapitalizationProps {
  adoption?: AdoptionDataType;
  capitalization?: CapitalizationData;
  capitalizationMonths: number;
  hasAdoptionData: boolean;
  onRequestDeployment?: () => void;
}

const AdoptionCapitalization: React.FC<AdoptionCapitalizationProps> = ({
  adoption,
  capitalization,
  capitalizationMonths,
  hasAdoptionData,
  onRequestDeployment,
}) => (
  <section className="bg-white border-t border-b border-[#E8E8E8] py-12 px-4 md:px-12">
    <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
      {hasAdoptionData && adoption ? (
        <AdoptionPanel adoption={adoption} />
      ) : (
        <AdoptionDeploymentCTA onRequestDeployment={onRequestDeployment} />
      )}
      <CapitalizationPanel capitalization={capitalization} capitalizationMonths={capitalizationMonths} />
    </div>
  </section>
);

const AdoptionPanel: React.FC<{ adoption: AdoptionDataType }> = ({ adoption }) => (
  <div>
    <h2 className="text-2xl leading-tight font-medium">Votre utilisation de la Bible ce trimestre</h2>
    <div className="mt-6 grid grid-cols-2 gap-6">
      <AdoptionTile value={formatNumber(adoption.pricesConsulted)} label="Prix consultés" />
      <AdoptionTile value={formatNumber(adoption.anomaliesIdentified)} label="Anomalies identifiées" />
      <AdoptionTile value={formatNumber(adoption.costEstimatesProduced)} label="Chiffrages produits" />
      <AdoptionTile value={formatNumber(adoption.activeUsers)} label="Utilisateurs actifs" />
    </div>
    <div className="mt-6 h-[100px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={adoption.weeklyActivity} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
          <defs>
            <linearGradient id="adoptionGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={VIOLET} stopOpacity={0.5} />
              <stop offset="100%" stopColor={VIOLET} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="week" hide />
          <YAxis hide />
          <Tooltip formatter={(v: number) => `${v} prix`} labelFormatter={(l) => `Semaine ${l}`} />
          <Area type="monotone" dataKey="pricesConsulted" stroke={VIOLET} strokeWidth={2} fill="url(#adoptionGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const AdoptionTile: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <div className="text-[32px] md:text-[40px] leading-tight font-medium tracking-tight">{value}</div>
    <div className="mt-1 text-sm text-[#6B6B6B]">{label}</div>
  </div>
);

const AdoptionDeploymentCTA: React.FC<{ onRequestDeployment?: () => void }> = ({ onRequestDeployment }) => (
  <div className="bg-[#F5F0FF] rounded-2xl p-10 md:p-12 text-center flex flex-col items-center justify-center">
    <RocketLaunchIcon className="w-10 h-10 text-[#8b5cf6]" aria-hidden="true" />
    <p className="mt-4 text-base md:text-lg text-[#2F2F2F] leading-relaxed max-w-md">
      Une fois déployé, suivez ici l’adoption de la Bible par vos équipes en temps réel.
    </p>
    {onRequestDeployment && (
      <button
        onClick={onRequestDeployment}
        className="mt-6 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white px-6 py-3 rounded-full font-medium transition-colors"
      >
        → Planifier le déploiement
      </button>
    )}
  </div>
);

interface CapitalizationPanelProps {
  capitalization?: CapitalizationData;
  capitalizationMonths: number;
}

const CapitalizationPanel: React.FC<CapitalizationPanelProps> = ({ capitalization, capitalizationMonths }) => {
  const currentCount = capitalization?.currentPriceCount ?? 0;
  const progression = useMemo(
    () => capitalization?.monthlyProgression ?? [],
    [capitalization]
  );
  const showChart = capitalizationMonths >= 3 && progression.length >= 2;

  const growthText = useMemo(() => {
    if (progression.length < 2) return null;
    const first = progression[0].priceCount;
    const last = progression[progression.length - 1].priceCount;
    if (first === 0) return `de 0 → ${formatNumber(last)} prix`;
    const pct = Math.round(((last - first) / first) * 100);
    return `de ${formatNumber(first)} → ${formatNumber(last)} prix (+${pct}%)`;
  }, [progression]);

  return (
    <div>
      <h2 className="text-2xl leading-tight font-medium">Votre bible propriétaire</h2>
      <div className="mt-6">
        <div className="text-[40px] md:text-[48px] leading-none font-medium tracking-tight text-[#8b5cf6]">
          {formatNumber(currentCount)}
        </div>
        <div className="mt-2 text-base text-[#2F2F2F]">prix capitalisés</div>
      </div>

      {showChart ? (
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider text-[#6B6B6B] font-medium">
            Progression ({progression.length} derniers mois)
          </div>
          <div className="mt-3 h-[100px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={progression} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                <defs>
                  <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={VIOLET} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={VIOLET} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" hide />
                <YAxis hide />
                <Tooltip formatter={(v: number) => `${formatNumber(v)} prix`} />
                <Area type="monotone" dataKey="priceCount" stroke={VIOLET} strokeWidth={2} fill="url(#capGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {growthText && <div className="mt-2 text-sm text-[#6B6B6B]">{growthText}</div>}
        </div>
      ) : (
        <div className="mt-6 text-sm text-[#6B6B6B] leading-relaxed">
          Votre référentiel grandit à chaque opération.
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// AuditFooter
// -------------------------------------------------------------------

interface AuditFooterProps {
  scope: ScopeData;
  mode: 'prospect' | 'client';
  onRequestDeployment?: () => void;
  onDownloadReport?: () => void;
}

const AuditFooter: React.FC<AuditFooterProps> = ({ scope, mode, onRequestDeployment, onDownloadReport }) => {
  const hasScope = scope.dceCount > 0;
  return (
    <section className="bg-[#FAFAFA] py-10 px-4 md:px-12">
      <div className="max-w-[960px] mx-auto text-center">
        {hasScope ? (
          <p className="text-sm text-[#6B6B6B] leading-relaxed">
            Périmètre : {formatNumber(scope.dceCount)} DCE audités · {formatPeriod(scope.periodStart, scope.periodEnd)}
            {scope.totalMarketValue > 0 && (
              <> · {formatEuro(scope.totalMarketValue)} de marchés</>
            )}
            {scope.lineItemsAnalyzed > 0 && (
              <> · {formatNumber(scope.lineItemsAnalyzed)} postes analysés</>
            )}
          </p>
        ) : (
          <p className="text-sm text-[#6B6B6B] leading-relaxed">
            Les premières données apparaîtront dès l’import de vos premiers DCE.
          </p>
        )}

        <div className="mt-6 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4">
          {onRequestDeployment && (
            <button
              onClick={onRequestDeployment}
              className={
                'flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-colors ' +
                (mode === 'prospect'
                  ? 'bg-[#8b5cf6] text-white hover:bg-[#7c3aed]'
                  : 'border border-[#2F2F2F] text-[#2F2F2F] hover:bg-[#2F2F2F] hover:text-white')
              }
            >
              <RocketLaunchIcon className="w-5 h-5" aria-hidden="true" />
              Planifier le déploiement
            </button>
          )}
          {onDownloadReport && (
            <button
              onClick={onDownloadReport}
              className="flex items-center gap-2 border border-[#2F2F2F] text-[#2F2F2F] px-6 py-3 rounded-full font-medium hover:bg-[#2F2F2F] hover:text-white transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" aria-hidden="true" />
              Télécharger le rapport PDF
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ObservatoireROI;
